import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Table, Input, Tag, Typography, Space, Spin, Select, Tooltip, Button, Drawer, Form, message, Badge, Alert,
} from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageState from '../components/page/PageState';
import PageFilter from '../components/page/PageFilter';
import DataCard from '../components/page/DataCard';
import { SearchOutlined, EditOutlined, LinkOutlined, HolderOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { useApp } from '../context/AppContext';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import DraggableHeaderCell from '../components/DraggableHeaderCell';
import {
    DASHBOARD_SORTER_TOOLTIP,
    DASHBOARD_TABLE_STICKY,
    dashboardPagination,
} from '../utils/adminDashboardTable';
import { naturalCompare, sortByField, sortTaxonomyMappingField } from '../utils/tableSorters';

const { Text } = Typography;

// ── Framework column config ──────────────────────────────────────────────────
const FRAMEWORK_COLUMNS = [
    { key: 'iso27001', dataIndex: 'isoControl', title: 'ISO 27001:2022', color: '#0958d9', width: 130 },
    { key: 'pciDss', dataIndex: 'pciDss', title: 'PCI DSS v4.0', color: '#1677ff', width: 130 },
    { key: 'gdpr', dataIndex: 'gdpr', title: 'GDPR', color: '#52c41a', width: 140 },
    { key: 'nistCsf', dataIndex: 'nistCsf', title: 'NIST CSF', color: '#722ed1', width: 130 },
    { key: 'iso27701', dataIndex: 'iso27701', title: 'ISO 27701', color: '#fa8c16', width: 140 },
    { key: 'fedramp', dataIndex: 'fedramp', title: 'FedRAMP', color: '#eb2f96', width: 200 },
    { key: 'iso42001', dataIndex: 'iso42001', title: 'ISO 42001', color: '#13c2c2', width: 100 },
    { key: 'iso22301', dataIndex: 'iso22301', title: 'ISO 22301', color: '#faad14', width: 100 },
];

const FRAMEWORK_BY_KEY = Object.fromEntries(FRAMEWORK_COLUMNS.map((c) => [c.key, c]));
const DEFAULT_ORDER = FRAMEWORK_COLUMNS.map((c) => c.key);
const LS_FW_ORDER_KEY = 'policylens:taxonomy-fw-order';

const FRAMEWORK_LABELS = Object.fromEntries(FRAMEWORK_COLUMNS.filter(c => c.key !== 'iso27001').map(c => [c.key, c.title]));

function toIsoLabel(ctrlNumber) {
    const t = String(ctrlNumber || '').trim();
    if (!t) return '';
    return /^A\./i.test(t) ? `A.${t.replace(/^A\./i, '')}` : `A.${t}`;
}

function loadFwOrder() {
    try {
        const raw = localStorage.getItem(LS_FW_ORDER_KEY);
        if (!raw) return DEFAULT_ORDER;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed) || parsed.length !== DEFAULT_ORDER.length) return DEFAULT_ORDER;
        const valid = parsed.every((k) => FRAMEWORK_BY_KEY[k]);
        return valid ? parsed : DEFAULT_ORDER;
    } catch {
        return DEFAULT_ORDER;
    }
}

function saveFwOrder(order) {
    try { localStorage.setItem(LS_FW_ORDER_KEY, JSON.stringify(order)); } catch { /* ignore */ }
}

// ── Resizable column width persistence ───────────────────────────────────────
const LS_CW_PREFIX = 'policylens:colwidths:control-taxonomy-v3:';
function loadColWidths() {
    try {
        const raw = localStorage.getItem(LS_CW_PREFIX);
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
}
function saveColWidths(w) {
    try { localStorage.setItem(LS_CW_PREFIX, JSON.stringify(w)); } catch { /* */ }
}

const ControlTaxonomy = () => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const instanceRef = useRef(instance);
    const accountRef = useRef(activeAccount);
    useEffect(() => { instanceRef.current = instance; }, [instance]);
    useEffect(() => { accountRef.current = activeAccount; }, [activeAccount]);

    const { fetchTaxonomyPendingCount, taxonomyPendingCount } = useApp();

    const [mappings, setMappings] = useState([]);
    const [pendingControls, setPendingControls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingLoading, setPendingLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [frameworkFilter, setFrameworkFilter] = useState('all');
    const [viewMode, setViewMode] = useState('mappings');

    const [mappingDrawerOpen, setMappingDrawerOpen] = useState(false);
    const [mappingSaving, setMappingSaving] = useState(false);
    const [mappingForm] = Form.useForm();
    const [editingMapping, setEditingMapping] = useState(null);
    const [registryControlId, setRegistryControlId] = useState(null);

    // Framework column order — first element is the "primary" framework
    const [frameworkOrder, setFrameworkOrder] = useState(loadFwOrder);
    const [colWidths, setColWidths] = useState(loadColWidths);

    const primaryFramework = frameworkOrder[0];
    const primaryFwConfig = FRAMEWORK_BY_KEY[primaryFramework];

    const getToken = useCallback(async () => {
        const req = { ...apiTokenRequest, account: accountRef.current };
        try {
            return (await instanceRef.current.acquireTokenSilent(req)).accessToken;
        } catch (err) {
            // Expired session / consent needed: a silent failure would otherwise be a permanent 401.
            if (err?.name === 'InteractionRequiredAuthError' || err?.errorCode === 'interaction_required' || err?.errorCode === 'consent_required' || err?.errorCode === 'login_required') {
                return (await instanceRef.current.acquireTokenPopup(req)).accessToken;
            }
            throw err;
        }
    }, []);

    // Parse a response without trusting the body to be JSON; turn 401/403 into readable text.
    const readApiResponse = useCallback(async (res) => {
        let json = null;
        try { json = await res.json(); } catch { /* non-JSON body (e.g. gateway 401) */ }
        if (res.status === 401) {
            return { ok: false, message: 'Your session is not authorised for this request (401). Sign out and sign in again; if it persists, ask a platform admin to check your access.' };
        }
        if (res.status === 403) {
            return { ok: false, message: 'Control Taxonomy is restricted to ProLense platform admins (403).' };
        }
        if (!res.ok || !json?.success) {
            return { ok: false, message: json?.error?.message || `Request failed (HTTP ${res.status})` };
        }
        return { ok: true, json };
    }, []);

    // ── Fetch mappings (sends primaryFramework to backend) ───────────────
    const fetchMappings = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const token = await getToken();
            const params = new URLSearchParams();
            if (primaryFramework && primaryFramework !== 'iso27001') {
                params.set('primaryFramework', primaryFramework);
            }
            const url = `${apiConfig.baseUrl}/api/v1/control-taxonomy${params.toString() ? '?' + params : ''}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const parsed = await readApiResponse(res);
            if (parsed.ok) {
                setMappings(parsed.json.data.mappings);
            } else {
                setError(parsed.message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [getToken, primaryFramework, readApiResponse]);

    const fetchPending = useCallback(async () => {
        try {
            setPendingLoading(true);
            const token = await getToken();
            const res = await fetch(
                `${apiConfig.baseUrl}/api/v1/iso27001/controls?taxonomyReviewStatus=pending`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const parsed = await readApiResponse(res);
            if (parsed.ok) setPendingControls(parsed.json.data.controls || []);
            else setPendingControls([]);
        } catch { setPendingControls([]); }
        finally { setPendingLoading(false); }
    }, [getToken, readApiResponse]);

    useEffect(() => { fetchMappings(); }, [fetchMappings]);
    useEffect(() => { fetchTaxonomyPendingCount(); }, [fetchTaxonomyPendingCount]);
    useEffect(() => { if (viewMode === 'pending') fetchPending(); }, [viewMode, fetchPending]);

    // ── Client-side filtering ────────────────────────────────────────────
    const filtered = useMemo(() => {
        let data = mappings;
        if (search) {
            const q = search.toLowerCase();
            data = data.filter((m) =>
                m.isoControl?.toLowerCase().includes(q) ||
                m.isoControlName?.toLowerCase().includes(q) ||
                (m.primaryControlName && m.primaryControlName.toLowerCase().includes(q)) ||
                (m.pciDss && m.pciDss.toLowerCase().includes(q)) ||
                (m.gdpr && m.gdpr.toLowerCase().includes(q)) ||
                (m.nistCsf && m.nistCsf.toLowerCase().includes(q)) ||
                (m.fedramp && m.fedramp.toLowerCase().includes(q)) ||
                (m.iso27701 && m.iso27701.toLowerCase().includes(q)) ||
                (m.iso42001 && m.iso42001.toLowerCase().includes(q)) ||
                (m.iso22301 && m.iso22301.toLowerCase().includes(q))
            );
        }
        if (frameworkFilter !== 'all') {
            data = data.filter((m) => m[frameworkFilter] && m[frameworkFilter] !== 'N/A');
        }
        // Server orders isoControl as text (A.5.1, A.5.10, A.5.2); present natural order by default.
        if (primaryFramework !== 'iso27001') return data;
        return [...data].sort((a, b) => naturalCompare(a.isoControl, b.isoControl));
    }, [mappings, search, frameworkFilter, primaryFramework]);

    // ── Render helpers ───────────────────────────────────────────────────
    const renderCell = useCallback((value, color) => {
        if (!value || value === 'N/A') return <Text type="secondary" style={{ fontSize: 12 }}>-</Text>;
        return (
            <Tooltip title={value}>
                <Tag color={color} style={{ fontSize: 12, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {value}
                </Tag>
            </Tooltip>
        );
    }, []);

    // ── Mapping drawer ───────────────────────────────────────────────────
    const openEditMapping = useCallback((row) => {
        setEditingMapping(row);
        setRegistryControlId(null);
        mappingForm.setFieldsValue({
            isoControl: row.isoControl,
            isoControlName: row.isoControlName,
            pciDss: row.pciDss || '',
            gdpr: row.gdpr || '',
            nistCsf: row.nistCsf || '',
            iso27701: row.iso27701 || '',
            fedramp: row.fedramp || '',
            iso42001: row.iso42001 || '',
            iso22301: row.iso22301 || '',
            nis2: row.nis2 || '',
        });
        setMappingDrawerOpen(true);
    }, [mappingForm]);

    const openCreateFromRegistry = useCallback((ctrl) => {
        setEditingMapping(null);
        setRegistryControlId(ctrl.id);
        mappingForm.setFieldsValue({
            isoControl: toIsoLabel(ctrl.ctrlNumber),
            isoControlName: ctrl.ctrlName,
            pciDss: '', gdpr: '', nistCsf: '', iso27701: '', fedramp: '', iso42001: '', iso22301: '', nis2: '',
        });
        setMappingDrawerOpen(true);
    }, [mappingForm]);

    const submitMapping = async () => {
        const values = await mappingForm.validateFields();
        const token = await getToken();
        if (!token) { message.error('Not authenticated'); return; }
        const body = {
            isoControl: values.isoControl.trim(),
            isoControlName: values.isoControlName.trim(),
            pciDss: values.pciDss || null,
            gdpr: values.gdpr || null,
            nistCsf: values.nistCsf || null,
            iso27701: values.iso27701 || null,
            fedramp: values.fedramp || null,
            iso42001: values.iso42001 || null,
            iso22301: values.iso22301 || null,
            nis2: values.nis2 || null,
        };
        if (registryControlId) body.registryControlId = registryControlId;
        setMappingSaving(true);
        try {
            if (editingMapping) {
                const res = await fetch(`${apiConfig.baseUrl}/api/v1/control-taxonomy/mappings/${editingMapping.id}`, {
                    method: 'PATCH',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...body, registryControlId: registryControlId || undefined }),
                });
                const json = await res.json();
                if (!res.ok || !json.success) throw new Error(json.error?.message || res.statusText);
                message.success('Mapping saved');
            } else {
                const res = await fetch(`${apiConfig.baseUrl}/api/v1/control-taxonomy/mappings`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                const json = await res.json();
                if (!res.ok || !json.success) throw new Error(json.error?.message || res.statusText);
                message.success('Cross-framework mapping created');
            }
            setMappingDrawerOpen(false);
            setEditingMapping(null);
            setRegistryControlId(null);
            await fetchMappings();
            await fetchPending();
            fetchTaxonomyPendingCount();
        } catch (e) {
            message.error(e.message || 'Save failed');
        } finally {
            setMappingSaving(false);
        }
    };

    // ── DnD ──────────────────────────────────────────────────────────────
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setFrameworkOrder((prev) => {
            const oldIdx = prev.indexOf(active.id);
            const newIdx = prev.indexOf(over.id);
            if (oldIdx === -1 || newIdx === -1) return prev;
            const next = arrayMove(prev, oldIdx, newIdx);
            saveFwOrder(next);
            return next;
        });
    }, []);

    // ── Dynamic columns ──────────────────────────────────────────────────
    const setColumnWidth = useCallback((colKey, w) => {
        setColWidths((prev) => {
            const next = { ...prev, [colKey]: w };
            saveColWidths(next);
            return next;
        });
    }, []);

    const taxonomyColumns = useMemo(() => {
        const cols = [];

        // Position 1: Primary framework
        const pf = FRAMEWORK_BY_KEY[frameworkOrder[0]];
        cols.push({
            title: pf.title,
            dataIndex: pf.dataIndex,
            key: pf.key,
            width: colWidths[pf.key] ?? pf.width,
            sorter: pf.key === 'iso27001'
                ? (a, b) => naturalCompare(a.isoControl, b.isoControl)
                : sortTaxonomyMappingField(pf.dataIndex),
            render: (text) => <Text strong style={{ fontSize: 13 }}>{text || '-'}</Text>,
            onHeaderCell: () => ({
                dndId: pf.key,
                width: colWidths[pf.key] ?? pf.width,
                minWidth: 60,
                onResize: (w) => setColumnWidth(pf.key, w),
            }),
        });

        // Position 2: Control Name (not draggable)
        cols.push({
            title: 'Control Name',
            dataIndex: 'primaryControlName',
            key: '__controlName',
            width: colWidths['__controlName'] ?? 240,
            ellipsis: { showTitle: true },
            sorter: sortByField('primaryControlName'),
            render: (text) => {
                const display = text || '-';
                return <Tooltip title={display}><span style={{ fontSize: 13 }}>{display}</span></Tooltip>;
            },
            onHeaderCell: () => ({
                width: colWidths['__controlName'] ?? 240,
                minWidth: 80,
                onResize: (w) => setColumnWidth('__controlName', w),
            }),
        });

        // Positions 3+: Remaining frameworks in order
        for (let i = 1; i < frameworkOrder.length; i++) {
            const fwKey = frameworkOrder[i];
            const fw = FRAMEWORK_BY_KEY[fwKey];
            if (!fw) continue;
            cols.push({
                title: fw.title,
                dataIndex: fw.dataIndex,
                key: fw.key,
                width: colWidths[fw.key] ?? fw.width,
                sorter: fw.key === 'iso27001'
                    ? (a, b) => naturalCompare(a.isoControl, b.isoControl)
                    : sortTaxonomyMappingField(fw.dataIndex),
                render: (v) => renderCell(v, fw.color),
                onHeaderCell: () => ({
                    dndId: fw.key,
                    width: colWidths[fw.key] ?? fw.width,
                    minWidth: 60,
                    onResize: (w) => setColumnWidth(fw.key, w),
                }),
            });
        }

        // Last: Edit (fixed right, not draggable)
        cols.push({
            title: '',
            key: 'edit',
            width: 88,
            fixed: 'right',
            render: (_, row) => (
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditMapping(row)}>
                    Edit
                </Button>
            ),
            onHeaderCell: () => ({ width: 88 }),
        });

        return cols;
    }, [frameworkOrder, colWidths, renderCell, openEditMapping, setColumnWidth]);

    const tableComponents = useMemo(() => ({
        header: { cell: DraggableHeaderCell },
    }), []);

    const taxonomyTableScrollX = useMemo(() => {
        const sum = taxonomyColumns.reduce((acc, col) => acc + (typeof col.width === 'number' ? col.width : 120), 0);
        return sum > 0 ? sum : 1400;
    }, [taxonomyColumns]);

    // ── Pending columns ──────────────────────────────────────────────────
    const pendingColumns = useMemo(
        () => [
            { title: 'Control', dataIndex: 'ctrlNumber', key: 'ctrlNumber', width: 100, render: (n) => <Tag color="blue">{n}</Tag> },
            { title: 'Name', dataIndex: 'ctrlName', key: 'ctrlName', ellipsis: { showTitle: true } },
            { title: 'Theme', dataIndex: 'controlTheme', key: 'controlTheme', width: 140, render: (t) => (t ? <Tag>{t}</Tag> : '-') },
            {
                title: 'Action', key: 'act', width: 268, align: 'right',
                render: (_, ctrl) => (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
                        <Tooltip title="Map this control to PCI DSS, GDPR, NIST CSF, and other frameworks">
                            <Button type="primary" size="small" icon={<LinkOutlined />} onClick={() => openCreateFromRegistry(ctrl)}>
                                Add cross-framework mapping
                            </Button>
                        </Tooltip>
                    </div>
                ),
            },
        ],
        [openCreateFromRegistry]
    );

    // ── Info banner: non-ISO primary with no control names ───────────────
    const showEmptyNameBanner = primaryFramework !== 'iso27001' && mappings.length > 0 &&
        !mappings.some((m) => m.primaryControlName);

    // ── Loading / error states ───────────────────────────────────────────
    if (loading && viewMode === 'mappings') {
        return <PageState status="loading" message="Loading control taxonomy..." />;
    }
    if (error) {
        return <PageState status="error" message={error} />;
    }

    return (
        <PageShell width="wide">
            <PageHeader
                title="Control Taxonomy - Cross-Framework Mapping"
                subtitle="Drag column headers to set the primary framework. Control names resolve from the primary framework's Policy Hub data."
            />

            <Space wrap>
                <PageFilter
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                        { label: 'All mappings', value: 'mappings' },
                        {
                            value: 'pending',
                            label: (
                                <Badge dot={taxonomyPendingCount > 0} size="small" offset={[6, 0]}
                                    title={taxonomyPendingCount > 0 ? `${taxonomyPendingCount} control(s) need cross-framework mapping` : undefined}>
                                    <span aria-label={taxonomyPendingCount > 0 ? `Pending from Policy Hub, ${taxonomyPendingCount} to review` : 'Pending from Policy Hub'}>
                                        Pending from Policy Hub
                                    </span>
                                </Badge>
                            ),
                        },
                    ]}
                />
            </Space>

            {viewMode === 'mappings' && (
                <>
                    <DataCard size="small">
                        <Space wrap size="middle">
                            <Input
                                placeholder="Search controls, clauses, articles..."
                                prefix={<SearchOutlined />}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                allowClear
                                style={{ width: 320 }}
                            />
                            <Select
                                value={frameworkFilter}
                                onChange={setFrameworkFilter}
                                style={{ width: 220 }}
                                options={[
                                    { value: 'all', label: 'All Frameworks' },
                                    ...Object.entries(FRAMEWORK_LABELS).map(([key, label]) => ({
                                        value: key,
                                        label: `Has ${label} mapping`,
                                    })),
                                ]}
                            />
                            <Text type="secondary">
                                {filtered.length} of {mappings.length} controls
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                <HolderOutlined /> Drag column headers to reorder &middot; Primary: <strong>{primaryFwConfig?.title}</strong>
                            </Text>
                        </Space>
                    </DataCard>

                    {showEmptyNameBanner && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message={`Control names for ${primaryFwConfig?.title} will appear when its Policy Hub data is uploaded.`}
                        />
                    )}

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={frameworkOrder} strategy={horizontalListSortingStrategy}>
                            <Table
                                className="policylens-data-table policylens-control-taxonomy-mappings-table"
                                columns={taxonomyColumns}
                                components={tableComponents}
                                dataSource={filtered}
                                rowKey="id"
                                size="small"
                                sticky={DASHBOARD_TABLE_STICKY}
                                scroll={{ x: taxonomyTableScrollX }}
                                pagination={dashboardPagination({
                                    pageSize: 20,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} mappings`,
                                })}
                                showSorterTooltip={DASHBOARD_SORTER_TOOLTIP}
                                tableLayout="fixed"
                                bordered
                                style={{ fontSize: 13 }}
                            />
                        </SortableContext>
                    </DndContext>
                </>
            )}

            {viewMode === 'pending' && (
                <DataCard size="small">
                    <Spin spinning={pendingLoading}>
                        <div style={{ width: '100%', overflowX: 'auto' }}>
                            <Table
                                className="policylens-data-table policylens-pending-taxonomy-table"
                                rowKey="id"
                                columns={pendingColumns}
                                dataSource={pendingControls}
                                tableLayout="fixed"
                                pagination={{ pageSize: 10, showSizeChanger: false }}
                                locale={{ emptyText: 'No controls awaiting taxonomy review' }}
                                size="small"
                            />
                        </div>
                    </Spin>
                </DataCard>
            )}

            <Drawer
                title={editingMapping ? `Edit mapping - ${editingMapping.isoControl}` : 'Add cross-framework mapping'}
                width={480}
                open={mappingDrawerOpen}
                onClose={() => { setMappingDrawerOpen(false); setEditingMapping(null); setRegistryControlId(null); }}
                extra={(
                    <Space>
                        <Button onClick={() => { setMappingDrawerOpen(false); setEditingMapping(null); setRegistryControlId(null); }}>Cancel</Button>
                        <Button type="primary" loading={mappingSaving} onClick={submitMapping}>Save</Button>
                    </Space>
                )}
                destroyOnClose
            >
                <Form form={mappingForm} layout="vertical">
                    <Form.Item name="isoControl" label="ISO control ID" rules={[{ required: true }]}>
                        <Input placeholder="A.5.16" disabled={!!editingMapping} />
                    </Form.Item>
                    <Form.Item name="isoControlName" label="Control name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="pciDss" label="PCI DSS v4.0"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="gdpr" label="GDPR"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="nistCsf" label="NIST CSF"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="iso27701" label="ISO 27701"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="fedramp" label="FedRAMP"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="iso42001" label="ISO 42001"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="iso22301" label="ISO 22301"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="nis2" label="NIS2"><Input.TextArea rows={2} /></Form.Item>
                </Form>
            </Drawer>
        </PageShell>
    );
};

export default ControlTaxonomy;
